import { useAppSelector } from '@/hooks'
import { InProgressUtilityProgramUnenrollment } from '@/reducers/inProgressUtilityProgramUnenrollments'
import { selectVehicleById } from '@/selectors'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'
import { thermostatsCollection } from '@/reducers/thermostats'
import { energySystemCollection } from '@/reducers/energySystems'

function getVehicles(
  inProgressUnenrollment: InProgressUtilityProgramUnenrollment | undefined,
) {
  const vehicleIds = inProgressUnenrollment?.deviceSelection.vehicleIds ?? []
  const vehicles = useAppSelector((state) =>
    vehicleIds
      .map((id) => selectVehicleById(state, id))
      .filter((v) => v !== undefined),
  )
  const isLoadingOrIdle = useAppSelector(
    (state) => state.vehicles.isLoading || !state.vehicles.hasFetched,
  )
  return { vehicles, isLoadingOrIdle }
}

function useThermostats(
  inProgressUnenrollment: InProgressUtilityProgramUnenrollment | undefined,
) {
  const thermostatIds =
    inProgressUnenrollment?.deviceSelection.thermostatIds ?? []
  const { data, isLoadingOrIdle } = thermostatsCollection.useFetch()
  const thermostats = data.filter((t) => thermostatIds.includes(t.id))
  return { thermostats, isLoadingOrIdle }
}

function useChargers(
  inProgressUnenrollment: InProgressUtilityProgramUnenrollment | undefined,
) {
  const chargerIds = inProgressUnenrollment?.deviceSelection.chargerIds ?? []
  const { data, isLoadingOrIdle } = vehicleChargersCollection.useFetch()
  const chargers = data.filter((c) => chargerIds.includes(c.id))
  return { chargers, isLoadingOrIdle }
}

function useEnergySystems(
  inProgressUnenrollment: InProgressUtilityProgramUnenrollment | undefined,
) {
  const energySystemIds =
    inProgressUnenrollment?.deviceSelection.energySystemIds ?? []
  const { data, isLoadingOrIdle } =
    energySystemCollection.useFetch('energy-systems')
  const energySystems = data.filter((e) =>
    energySystemIds.includes(String(e.id)),
  )
  return { energySystems, isLoadingOrIdle }
}

export function useInProgressUnenrollmenDevices(
  inProgressUnenrollment: InProgressUtilityProgramUnenrollment,
) {
  const { vehicles, isLoadingOrIdle: vehiclesLoadingOrIdle } = getVehicles(
    inProgressUnenrollment,
  )
  const { thermostats, isLoadingOrIdle: thermostatsLoadingOrIdle } =
    useThermostats(inProgressUnenrollment)
  const { chargers, isLoadingOrIdle: chargersLoadingOrIdle } = useChargers(
    inProgressUnenrollment,
  )
  const { energySystems, isLoadingOrIdle: energySystemsLoadingOrIdle } =
    useEnergySystems(inProgressUnenrollment)

  const isLoadingOrIdle =
    vehiclesLoadingOrIdle ||
    thermostatsLoadingOrIdle ||
    chargersLoadingOrIdle ||
    energySystemsLoadingOrIdle
  return { vehicles, thermostats, chargers, energySystems, isLoadingOrIdle }
}
